type DraftEnvelope = {
  data: Record<string, unknown>;
  savedAt: string;
};

type OnErrorCallback = (error: unknown, context: string) => void;

const DEFAULT_MAX_AGE_DAYS = 7;

export function buildDraftKey(
  orgCode: string,
  draftKey: string,
  entityCode?: string,
): string {
  return `draft:${orgCode}:${draftKey}:${entityCode ?? "new"}`;
}

export function isDraftExpired(
  savedAt: string,
  maxAgeDays: number = DEFAULT_MAX_AGE_DAYS,
): boolean {
  const savedDate = new Date(savedAt);
  const now = new Date();
  const diffMs = now.getTime() - savedDate.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  return diffDays > maxAgeDays;
}

export function saveDraft(
  key: string,
  data: Record<string, unknown>,
  onError?: OnErrorCallback,
): void {
  if (typeof window === "undefined") return;

  const envelope: DraftEnvelope = {
    data,
    savedAt: new Date().toISOString(),
  };

  try {
    localStorage.setItem(key, JSON.stringify(envelope));
  } catch (error) {
    if (onError) {
      onError(error, `Failed to save draft for key "${key}"`);
    } else {
      console.warn(
        `[draft-storage] Failed to save draft for key "${key}"`,
        error,
      );
    }
  }
}

export function loadDraft(
  key: string,
  maxAgeDays: number = DEFAULT_MAX_AGE_DAYS,
  onError?: OnErrorCallback,
): { data: Record<string, unknown>; savedAt: string } | null {
  if (typeof window === "undefined") return null;

  const raw = localStorage.getItem(key);
  if (raw === null) return null;

  let envelope: DraftEnvelope;
  try {
    envelope = JSON.parse(raw) as DraftEnvelope;
  } catch (error) {
    if (onError) {
      onError(error, `Corrupted draft data for key "${key}", removing`);
    } else {
      console.warn(
        `[draft-storage] Corrupted draft data for key "${key}", removing`,
        error,
      );
    }
    localStorage.removeItem(key);
    return null;
  }

  if (isDraftExpired(envelope.savedAt, maxAgeDays)) {
    localStorage.removeItem(key);
    return null;
  }

  return { data: envelope.data, savedAt: envelope.savedAt };
}

export function clearDraft(key: string): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem(key);
}
